// src/components/lesson/diagrams/RightTriangle.tsx
//
// Right-triangle SVG with optional side / angle / vertex labels and a
// right-angle marker. Drives both lesson examples and tutor diagram
// embeds (see src/lib/tutor-diagram.ts).
//
// Coordinate convention: right angle at bottom-left, base along the
// bottom (horizontal), height up the left side (vertical), hypotenuse
// from top-left vertex down to bottom-right vertex.
//
//          C (top vertex, "topAngle")
//          |\
//   height | \   hypotenuse (hypLabel)
//   (left  |  \
//   leg)   |   \
//          |____\
//          A     B (bottom-right vertex, "bottomRightAngle")
//        right
//        angle
//        (bottomLeftAngle, default shows a right-angle marker)
//
// Numeric `base` and `height` set the SHAPE proportions only — labels
// like "√3", "x", "1/2" are arbitrary text the model can write. So a
// 30-60-90 question can pass base=1.732 (visually correct shape) but
// label the base "√3" (mathematically correct text).
//
// Params:
//   {
//     "base": 3, "height": 4,
//     "baseLabel": "3", "heightLabel": "4", "hypLabel": "5",
//     "topAngle": "53°", "bottomRightAngle": "37°",
//     "showRightAngle": true,            // default true
//     "label": "3-4-5 triangle"          // optional caption-like aria-label
//   }

type RightTriangleParams = {
  base?: number
  height?: number
  baseLabel?: string
  heightLabel?: string
  hypLabel?: string
  topAngle?: string
  bottomRightAngle?: string
  /** When true (default), draws a small square inside the right angle. */
  showRightAngle?: boolean
  label?: string
}

const W = 360
const H = 240
const PAD = 40

export default function RightTriangle({ params }: { params: Record<string, unknown> }) {
  const p = params as RightTriangleParams
  const baseRaw = typeof p.base === 'number' && p.base > 0 ? p.base : 3
  const heightRaw = typeof p.height === 'number' && p.height > 0 ? p.height : 4

  // Scale the triangle to fit inside the canvas while preserving the
  // ratio of base:height the caller asked for. Whichever dimension is
  // proportionally larger constrains the scale.
  const availW = W - 2 * PAD
  const availH = H - 2 * PAD
  const scale = Math.min(availW / baseRaw, availH / heightRaw)
  const baseLen = baseRaw * scale
  const heightLen = heightRaw * scale

  // Vertex coordinates. Right angle at bottom-left, anchored low-left
  // of the canvas so labels around the outside have room.
  const cx = (W - baseLen) / 2
  const cyBottom = H - PAD
  const A = { x: cx, y: cyBottom } // bottom-left (right angle)
  const B = { x: cx + baseLen, y: cyBottom } // bottom-right
  const C = { x: cx, y: cyBottom - heightLen } // top

  const showRight = p.showRightAngle !== false
  const rightSize = Math.min(14, Math.min(baseLen, heightLen) * 0.18)

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Right triangle'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Triangle body — filled lightly so the eye reads it as a shape, not 3 lines */}
      <polygon
        points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`}
        fill="rgb(34 211 238 / 0.10)"
        stroke="rgb(148 163 184)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Right-angle marker — a small square in the corner at A */}
      {showRight && (
        <polyline
          points={`${A.x + rightSize},${A.y} ${A.x + rightSize},${A.y - rightSize} ${A.x},${A.y - rightSize}`}
          fill="none"
          stroke="rgb(148 163 184)"
          strokeWidth="1.25"
        />
      )}

      {/* Side labels — placed outside the triangle, centered on each side */}
      {p.baseLabel && (
        <text
          x={(A.x + B.x) / 2}
          y={cyBottom + 18}
          fontSize="13"
          fontWeight="600"
          textAnchor="middle"
          fill="rgb(34 211 238)"
        >
          {p.baseLabel}
        </text>
      )}
      {p.heightLabel && (
        <text
          x={A.x - 10}
          y={(A.y + C.y) / 2}
          fontSize="13"
          fontWeight="600"
          textAnchor="end"
          dominantBaseline="middle"
          fill="rgb(34 211 238)"
        >
          {p.heightLabel}
        </text>
      )}
      {p.hypLabel && (
        // Position hypotenuse label just outside the midpoint, offset
        // perpendicular to the hypotenuse so it doesn't sit on the line.
        // For a triangle with right angle at bottom-left, the
        // hypotenuse runs from C (top-left) to B (bottom-right); the
        // outward normal points up-and-right, so push +x and -y.
        <text
          x={(B.x + C.x) / 2 + 12}
          y={(B.y + C.y) / 2 - 8}
          fontSize="13"
          fontWeight="600"
          textAnchor="middle"
          fill="rgb(34 211 238)"
        >
          {p.hypLabel}
        </text>
      )}

      {/* Angle labels at the non-right vertices */}
      {p.topAngle && (
        <text x={C.x + 12} y={C.y + 14} fontSize="11" textAnchor="start" fill="rgb(251 191 36)">
          {p.topAngle}
        </text>
      )}
      {p.bottomRightAngle && (
        <text x={B.x - 12} y={B.y - 6} fontSize="11" textAnchor="end" fill="rgb(251 191 36)">
          {p.bottomRightAngle}
        </text>
      )}
    </svg>
  )
}
